/**
 * localizedPath — prepend the current language prefix to internal paths.
 *
 * TWO VERSIONS:
 * 1. localizedPath(path) — module-level function, reads i18n.language at call time.
 *    Use in event handlers, callbacks, and non-reactive contexts.
 *
 * 2. useLocalizedPath() — React hook, returns a reactive `lp` function that
 *    updates when the language changes. Use in JSX/render to ensure links
 *    re-render when the language switches.
 */
import i18n from '@/i18n';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';

const SUPPORTED_LANGS = ['es','fr','de','ja','pt','zh','ru','ko','it','nl','pl','tr','ar','hi','id','th','vi','sv','da','no','fi','cs','ro','hu','uk'];

function buildPath(lang: string, path: string): string {
  // English: no prefix
  if (!SUPPORTED_LANGS.includes(lang)) return path;

  // Already prefixed: don't double-prefix
  if (path.startsWith(`/${lang}/`) || path === `/${lang}`) return path;

  // Strip any existing language prefix from the path first
  const langPrefixPattern = new RegExp(`^/(${SUPPORTED_LANGS.join('|')})(\/|$)`);
  const strippedPath = path.replace(langPrefixPattern, '/');

  // Root path special case
  if (strippedPath === '/') return `/${lang}`;

  return `/${lang}${strippedPath}`;
}

/** Module-level function — reads i18n.language at call time (non-reactive). */
export function localizedPath(path: string): string {
  return buildPath(i18n.language || 'en', path);
}

/** Alias */
export const lp = localizedPath;

/**
 * React hook — returns a reactive `lp` function that re-renders when language changes.
 * Use this in JSX to ensure links update when the user switches language.
 */
export function useLocalizedPath(): (path: string) => string {
  const { i18n: i18nInstance } = useTranslation();
  const lang = i18nInstance.language || 'en';

  return useCallback(
    (path: string) => buildPath(lang, path),
    [lang]
  );
}
